import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { FileText, ChevronDown, ChevronUp, AlertCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayNarrator } from '../../../mascot';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
import { getChildLastActivity } from '../shared/childActivity';
import { DataFreshness } from '../../shared/DataFreshness';
import { getAssessmentHistoryForLearner, getReport, type AssessmentReport, type AssessmentHistoryEntry } from '../../../assessment';
import { parseGroupNum } from '../shared/groupUtils';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { Skeleton } from '../../../components/shared/Skeleton';
import { getModuleIcon, getModuleColors } from '../../../content';
interface ToetsenPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  setViewingLearner: (id: string) => void;
}
export function ToetsenPage({
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  setViewingLearner
}: ToetsenPageProps) {
  const [history, setHistory] = useState<{
    entry: AssessmentHistoryEntry;
    report: AssessmentReport;
  }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!selectedChild) {
      setHistory([]);
      setIsLoading(false);
      setLoadError(false);
      return;
    }
    setIsLoading(true);
    setLoadError(false);
    const groupNum = parseGroupNum(selectedGroup);
    // AS-2026-07-P1-01: learner scope enforced by the accessor.
    getAssessmentHistoryForLearner(selectedChild.id, groupNum).then(async childHistory => {
      if (cancelled) return;
      const fullHistory = [];
      for (const entry of childHistory) {
        const report = await getReport(entry.attemptId);
        if (report) {
          fullHistory.push({
            entry,
            report
          });
        }
      }
      if (!cancelled) {
        setHistory(fullHistory);
        setIsLoading(false);
      }
    }).catch(() => {
      // AS-2026-07-P3-02: surface the failure instead of rendering the
      // "nog geen toetsen gemaakt" empty state over a broken read.
      if (!cancelled) {
        setHistory([]);
        setLoadError(true);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedChild?.id, selectedGroup]);
  if (!selectedChild) {
    return <PageLayout title="Toetsen" description="Bekijk de toetshistorie" data-id="element-8435" data-mp="3cdg53fvzq1ls">
        <div className="text-center py-20 text-slate-500 font-medium" data-id="element-8436" data-mp="1rume4zek73js">
          Geen data beschikbaar.
        </div>
      </PageLayout>;
  }
  const latestReport = history.length > 0 ? history[0].report : null;
  return <PageLayout title="Toetsen" description={`Bekijk de toetshistorie van ${selectedChild.displayName}`} actions={<DataFreshness updatedAt={getChildLastActivity(childData)} data-id="element-8438" data-mp="1mu89zsg2wdr1" />} data-id="element-8437" data-mp="1hzazsi72byck">
      <div className="max-w-4xl mx-auto space-y-8 pb-12" data-id="element-8439" data-mp="2amyexhpkdwrt">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8440" data-mp="2fli5jvf3rwqi" />

        {isLoading ? <div className="space-y-4" data-id="element-8441" data-mp="25mj3wwsr91d0">
            <Skeleton className="h-40 w-full rounded-2xl" data-id="element-8442" data-mp="25lhflqmqaidc" />
            <Skeleton className="h-20 w-full rounded-xl" data-id="element-8443" data-mp="q5selfqafa60" />
            <Skeleton className="h-20 w-full rounded-xl" data-id="element-8444" data-mp="q8eohm0ni1re" />
          </div> : loadError ? <Card className="flex flex-col items-center justify-center p-12 text-center border-2 border-amber-200 bg-amber-50/60" role="alert" data-id="element-8445" data-mp="u5sul4t1btf6">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border border-amber-100 mb-5" data-id="element-8446" data-mp="15mqroj0m45d0">
              <AlertCircle className="w-8 h-8 text-amber-600" data-id="element-8447" data-mp="vq5kbr14ei60" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-8448" data-mp="vo2kyge2vcom">
              Toetshistorie kon niet geladen worden
            </h3>
            <p className="text-slate-600 max-w-md mb-5" data-id="element-8449" data-mp="1oeb5ug000b6w">
              Er is niets verwijderd — we konden de gegevens van{' '}
              {selectedChild.displayName} even niet ophalen.
            </p>
            <button onClick={() => window.location.reload()} className="px-4 py-2 bg-white border border-amber-300 text-amber-900 rounded-lg text-sm font-bold hover:bg-amber-50 transition-colors" data-id="element-8450" data-mp="1feo4tvb6l7ed">
              Opnieuw proberen
            </button>
          </Card> : history.length === 0 ? <Card className="flex flex-col items-center justify-center p-12 text-center border-dashed border-2 border-slate-200 bg-slate-50/50" data-id="element-8451" data-mp="2fnw41m5ph795">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 mb-6" data-id="element-8452" data-mp="2on9gmnp4mby3">
              <DjayNarrator size="lg" emotion="thinking" data-id="element-8453" data-mp="2tehrzpa0o7dw" />
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-2" data-id="element-8454" data-mp="2ydc9mnw8zpwf">
              Nog geen toetsen gemaakt
            </h3>
            <p className="text-slate-500 max-w-md" data-id="element-8455" data-mp="33akrsohebtxo">
              Zodra {selectedChild.displayName} een niveaubepaling of toets
              afrondt, verschijnt hier de historie en het geschatte CITO-niveau.
            </p>
          </Card> : <div className="space-y-8" data-id="element-8456" data-mp="1czhnhmoknyug">
            {/* Hero: Latest Assessment */}
            {latestReport && <section data-id="element-8457" data-mp="1hy191lz978ma">
                <h2 className="text-lg font-black text-slate-900 mb-4" data-id="element-8458" data-mp="1hzcbnigyqabt">
                  Laatste Resultaat
                </h2>
                <Card className="p-6 sm:p-8 border-emerald-200 bg-emerald-50/50 shadow-sm" data-id="element-8459" data-mp="2lbkqphg81jyo">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6" data-id="element-8460" data-mp="2gc83gvlfuavk">
                    <div className="flex items-center gap-5" data-id="element-8461" data-mp="2ubg8w6872f87">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white border border-emerald-100 shadow-sm text-emerald-600" data-id="element-8462" data-mp="2pa0ma9qdc0np">
                        <FileText className="w-8 h-8" data-id="element-8463" data-mp="2z9hogh6evwex" />
                      </div>
                      <div data-id="element-8464" data-mp="2z7ez7zd327b9">
                        <div className="flex items-center gap-2 mb-1" data-id="element-8465" data-mp="3d1642ufvd30n">
                          <span className="text-sm font-bold text-emerald-800 uppercase tracking-wider" data-id="element-8466" data-mp="38z1a1ulie4dh">
                            Niveaubepaling
                          </span>
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md" data-id="element-8467" data-mp="3mwpvb3jhndj2">
                            Groep {latestReport.selectedGroup}
                          </span>
                        </div>
                        <div className="text-2xl font-black text-slate-900" data-id="element-8468" data-mp="afrhjdfuanf2">
                          {new Date(latestReport.completedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 self-start sm:self-center bg-white p-4 rounded-2xl border border-emerald-100 shadow-sm" data-id="element-8469" data-mp="3nnkqz0rtprj7">
                      <div className="text-right" data-id="element-8470" data-mp="3dwq6o1y5120e">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-8471" data-mp="2zw5mp2ghl05b">
                          Geschat Niveau
                        </div>
                        <div className="text-3xl font-black text-slate-800 leading-none" data-id="element-8472" data-mp="33zv8jk5s13mn">
                          {latestReport.overallEstimatedLevelCode}
                        </div>
                      </div>

                      {(() => {
                  const cito = levelCodeToCitoLevel(latestReport.overallEstimatedLevelCode, latestReport.selectedGroup);
                  if (!cito) return null;
                  return <div className={`flex items-center justify-center px-4 py-2 rounded-xl border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8473" data-mp="2wnyxana8klp8">
                            <span className={`text-xl font-black ${cito.color}`} data-id="element-8474" data-mp="2mq1e4e78hf3h">
                              {cito.label}
                            </span>
                          </div>;
                })()}
                    </div>
                  </div>
                </Card>
              </section>}

            {/* History List */}
            <section data-id="element-8475" data-mp="x6t1hcva38kz">
              <h2 className="text-lg font-black text-slate-900 mb-4" data-id="element-8476" data-mp="xagwkggdg31t">
                Historie
              </h2>
              <div className="space-y-3" data-id="element-8477" data-mp="176jxjizhlvn6">
                {history.map(({
              entry,
              report
            }, index) => {
              const isExpanded = expandedId === entry.attemptId;
              const prevReport = history[index + 1]?.report;
              const cito = levelCodeToCitoLevel(report.overallEstimatedLevelCode, report.selectedGroup);
              const panelId = `toets-panel-${entry.attemptId}`;
              return <Card key={entry.attemptId} className={`overflow-hidden transition-all duration-200 border-slate-200 ${isExpanded ? 'ring-2 ring-emerald-500 border-transparent shadow-md' : 'hover:border-emerald-300 hover:shadow-sm'}`} data-id="element-8478" data-mp="3mxpmxlme1nir" data-mpi={index}>
                      <div role="button" tabIndex={0} aria-expanded={isExpanded} aria-controls={panelId} onClick={() => setExpandedId(isExpanded ? null : entry.attemptId)} onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(isExpanded ? null : entry.attemptId);
                  }
                }} className="p-4 sm:p-5 flex items-center justify-between gap-4 bg-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500" data-id="element-8479" data-mp="2nxns184ga1dc">
                        <div className="flex items-center gap-4" data-id="element-8480" data-mp="1mfdnglwok17h">
                          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400" data-id="element-8481" data-mp="3m3p41xjlnhk">
                            <FileText className="w-5 h-5" data-id="element-8482" data-mp="3vnicqudozlkv" />
                          </div>
                          <div data-id="element-8483" data-mp="3qop01468bylr">
                            <div className="font-bold text-slate-900" data-id="element-8484" data-mp="3lrf9tmb0niwv">
                              {new Date(entry.completedAt).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-8485" data-mp="32yupit9dum4o">
                              Groep {entry.selectedGroup} •{' '}
                              {entry.totalQuestions} vragen
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 sm:gap-6" data-id="element-8486" data-mp="2jdui4m8ec8ip">
                          <div className="text-right hidden sm:block" data-id="element-8487" data-mp="2of1m0a7f4nk3">
                            <div className="text-lg font-black text-slate-800" data-id="element-8488" data-mp="2aezbxa5cqyfe">
                              {report.overallEstimatedLevelCode}
                            </div>
                          </div>

                          {cito && <div className={`w-8 h-8 rounded-lg flex items-center justify-center border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8489" data-mp="326f1nd27h19u">
                              <span className={`text-sm font-black ${cito.color}`} data-id="element-8490" data-mp="2tb8ua9naeb57">
                                {cito.label}
                              </span>
                            </div>}

                          <div className="text-slate-400" data-id="element-8491" data-mp="3pwiwow1bvsp8">
                            {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-8492" data-mp="1ee5ygrbmvms2" /> : <ChevronDown className="w-5 h-5" data-id="element-8493" data-mp="19f34c1x6z0yv" />}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Sub-scores */}
                      <AnimatePresence initial={false} data-id="element-8494" data-mp="vh4pkveyl2iu">
                        {isExpanded && <motion.div id={panelId} key="panel" initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} className="border-t border-slate-100 bg-slate-50/50 overflow-hidden" data-id="element-8495" data-mp="qijqx0fcuqwi">
                            <div className="p-4 sm:p-5" data-id="element-8496" data-mp="zc7hs2r08cxo">
                              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8497" data-mp="14cc263v19rt6">
                                Scores per vak
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-8498" data-mp="1e2ybxhg2jo3y">
                                {report.estimatedLevels.map(mod => {
                          const IconComp = getModuleIcon(mod.moduleId);
                          const colors = getModuleColors(mod.moduleId);
                          const prevMod = prevReport?.estimatedLevels.find(m => m.moduleId === mod.moduleId);
                          const delta = prevMod ? mod.estimatedTier - prevMod.estimatedTier : 0;
                          return <div key={mod.moduleId} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100 shadow-sm" data-id="element-8499" data-mp="2kx4gkzf8yst3">
                                      <div className="flex items-center gap-3" data-id="element-8500" data-mp="2b5858mo5h66x">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg} ${colors.text}`} data-id="element-8501" data-mp="275qylpwzm306">
                                          <IconComp className="w-4 h-4" data-id="element-8502" data-mp="2yvcb70ot9i13" />
                                        </div>
                                        <div className="font-bold text-slate-700 text-sm" data-id="element-8503" data-mp="11yg7hun5do58">
                                          {mod.moduleTitle}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3" data-id="element-8504" data-mp="s7lkup5zomj9">
                                        {prevMod && <div className="flex items-center text-xs font-bold" data-id="element-8505" data-mp="ebhw3bdvx6e6">
                                            {delta > 0 ? <span className="text-emerald-500 flex items-center" aria-label="Gestegen sinds vorige toets" title="Gestegen sinds vorige toets" data-id="element-8506" data-mp="4c94gp6hs3uz">
                                                <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-8507" data-mp="3o5ku3i01brpz" />
                                              </span> : delta < 0 ? <span className="text-rose-500 flex items-center" aria-label="Gedaald sinds vorige toets" title="Gedaald sinds vorige toets" data-id="element-8508" data-mp="1qt1nq37b16q">
                                                <TrendingDown className="w-3 h-3 mr-0.5" data-id="element-8509" data-mp="6rytzmsy8mfq" />
                                              </span> : <span className="text-slate-300 flex items-center" aria-label="Gelijk gebleven" title="Gelijk gebleven" data-id="element-8510" data-mp="fo7yvgzp4d99">
                                                <Minus className="w-3 h-3 mr-0.5" data-id="element-8511" data-mp="20mt9vzoo9kg5" />
                                              </span>}
                                          </div>}
                                        <div className="text-sm font-black text-slate-900 bg-slate-100 px-2 py-1 rounded-md" data-id="element-8512" data-mp="1cpnkidr3a5gp">
                                          {mod.estimatedLevelCode}
                                        </div>
                                      </div>
                                    </div>;
                        })}
                              </div>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </Card>;
            })}
              </div>
            </section>

            {/* Disclaimer */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-8513" data-mp="rgjaqaxt0spj">
              <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-8514" data-mp="hox0agtaixnu" />
              <div className="text-sm text-blue-800 leading-relaxed" data-id="element-8515" data-mp="hmu2vbpt6293">
                <strong data-id="element-8516" data-mp="cpamo69jvfei">Let op:</strong> De getoonde niveaus zijn een
                inschatting op basis van de gemaakte opgaven in Meester Djay.
                Dit is geen officiële CITO-score en kan afwijken van de
                resultaten op school. Gebruik deze gegevens als indicatie van de
                groei.
              </div>
            </div>
          </div>}
      </div>
    </PageLayout>;
}